/**
 * Beauty Dent theme helpers for Dawn / Trade.
 * Keeps cart Standard Actions working, connects the custom header search to Shopify native predictive search,
 * and syncs the custom product gallery with selected product variants.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set(['cart-drawer:cart-drawer','cart-drawer-items:CartDrawer','cart-items:main-cart-items']);

function collectCartSections() {
  const sections = new Map();
  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try { entries = el.getSectionsToRender?.(); } catch { continue; }
    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;
      const mount = entry.selector ? (root.querySelector(entry.selector) ?? (entry.section ? root : null)) : document.getElementById(entry.id);
      if (!mount) continue;
      sections.set(sectionId, { mount, extractSelector: entry.selector || '.shopify-section' });
    }
  }
  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size ? `?sections=${[...sections.keys()].join(',')}` : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const cartData = await fetch(`${cartUrl}.js${sectionsQuery}`, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'external-refresh', cartData: cartData ?? undefined });
  }
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;
  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      return defaultHandler();
    },
  });
  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try { await refreshDawnCartUI(); }
      catch (error) { console.error('[Beauty Dent] Cart refresh failed; reloading.', error); window.location.reload(); }
      return result;
    },
  });
}

if (window.Shopify?.actions) initStandardActions();
else document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });

(function initBeautyDentNativeHeaderSearch() {
  const HEADER_SELECTOR = '[id^="BeautyDentHeader-"]';
  const FORM_SELECTOR = `${HEADER_SELECTOR} form.bdh__search`;
  const STYLE_ID = 'bdh-native-predictive-search-style';
  const LOADING_TEXT = 'Loading…';

  function installStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      ${HEADER_SELECTOR} .bdh__predictive-search{display:block;position:relative;min-width:0;width:100%}
      ${HEADER_SELECTOR} .bdh__predictive-search .bdh__search{width:100%}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search{top:calc(100% + 8px);right:0;left:0;z-index:500;width:100%;max-height:min(70vh,560px);overflow:auto;border:1px solid var(--bdh-border,#EDF0F4);border-radius:16px;background:#fff;color:var(--bdh-text,#344057);box-shadow:0 18px 42px rgba(20,26,38,.14);text-align:left}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search__results-groups-wrapper{gap:0;padding-bottom:0}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search__heading{font-family:var(--font-heading-family);font-weight:700;letter-spacing:.08em;color:#7A879C}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search__item{font-family:var(--font-body-family);color:var(--bdh-text,#344057)}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search__item-heading{font-family:var(--font-heading-family);font-size:1.4rem;line-height:1.35;color:inherit}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search__item-query-result mark{background:transparent;color:#7A879C}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search__item--term{font-weight:700;color:var(--bdh-accent,#E96611)}
      ${HEADER_SELECTOR} .bdh__predictive-search .predictive-search__image{border-radius:8px;background:#fff;object-fit:contain}
      ${HEADER_SELECTOR} .bdh__search-reset{display:grid;width:38px;height:38px;flex:0 0 38px;place-items:center;border:0;border-radius:50%;background:transparent;color:#98A5B8;font:700 22px/1 var(--font-body-family);cursor:pointer}
      ${HEADER_SELECTOR} .bdh__search-reset:hover{color:var(--bdh-accent,#E96611)}
      ${HEADER_SELECTOR} .bdh__search-reset.hidden{display:none!important}
      ${HEADER_SELECTOR} .bdh__search-input::-webkit-search-cancel-button{display:none}
      @media screen and (max-width:989px){${HEADER_SELECTOR} .bdh__mobile-search .bdh__predictive-search .predictive-search{top:calc(100% + 6px);max-height:60vh;border-radius:14px}}
    `;
    document.head.appendChild(style);
  }

  function ensurePrefix(form) {
    let prefix = form.querySelector('input[name="options[prefix]"]');
    if (!prefix) {
      prefix = document.createElement('input');
      prefix.type = 'hidden';
      prefix.name = 'options[prefix]';
      form.appendChild(prefix);
    }
    prefix.value = 'last';
  }

  function ensureResetButton(form) {
    let reset = form.querySelector('button[type="reset"]');
    if (reset) return reset;
    reset = document.createElement('button');
    reset.type = 'reset';
    reset.className = 'bdh__search-reset hidden';
    reset.setAttribute('aria-label', 'Clear search');
    reset.innerHTML = '<span aria-hidden="true">×</span>';
    const submit = form.querySelector('button[type="submit"]');
    if (submit) form.insertBefore(reset, submit);
    else form.appendChild(reset);
    return reset;
  }

  function ensurePredictiveMarkup(form, input, index) {
    const resultId = `BeautyDentHeaderPredictiveResults-${index + 1}`;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-owns', resultId);
    input.setAttribute('aria-controls', resultId);
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    let results = form.querySelector('[data-predictive-search]');
    if (!results) {
      results = document.createElement('div');
      results.id = resultId;
      results.className = 'predictive-search predictive-search--header bdh__native-predictive-results';
      results.tabIndex = -1;
      results.setAttribute('data-predictive-search', '');
      results.innerHTML = '<div class="predictive-search__loading-state"><span class="spinner"></span></div>';
      form.appendChild(results);
    }
    let status = form.querySelector('.predictive-search-status');
    if (!status) {
      status = document.createElement('span');
      status.className = 'predictive-search-status visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-hidden', 'true');
      form.appendChild(status);
    }
  }

  function syncInputs(value, sourceInput) {
    document.querySelectorAll(`${FORM_SELECTOR} input[type="search"]`).forEach((input) => { if (input !== sourceInput) input.value = value; });
  }

  function normalizeForm(form, input) {
    form.action = (window.routes && window.routes.search_url) || form.getAttribute('action') || '/search';
    form.method = 'get';
    form.setAttribute('role', 'search');
    input.name = 'q';
    ensurePrefix(form);
  }

  function manuallyBootPredictive(wrapper) {
    if (!customElements.get('predictive-search')) return;
    if (wrapper.dataset.bdhPredictiveBooted === 'true') return;
    if (wrapper.input && wrapper.predictiveSearchResults) { wrapper.dataset.bdhPredictiveBooted = 'true'; return; }
    if (typeof wrapper.setupEventListeners !== 'function') return;
    wrapper.input = wrapper.querySelector('input[type="search"]');
    wrapper.resetButton = wrapper.querySelector('button[type="reset"]');
    wrapper.cachedResults = wrapper.cachedResults || {};
    wrapper.predictiveSearchResults = wrapper.querySelector('[data-predictive-search]');
    wrapper.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    wrapper.isOpen = false;
    wrapper.abortController = new AbortController();
    wrapper.searchTerm = '';
    if (wrapper.input) {
      wrapper.input.form.addEventListener('reset', wrapper.onFormReset.bind(wrapper));
      wrapper.input.addEventListener('input', debounce((event) => wrapper.onChange(event), 300).bind(wrapper));
      wrapper.setupEventListeners();
      wrapper.dataset.bdhPredictiveBooted = 'true';
    }
  }

  function enhanceForm(form, index) {
    if (form.dataset.bdhNativeSearch === 'true') return null;
    const input = form.querySelector('input[type="search"]');
    if (!input) return null;
    form.dataset.bdhNativeSearch = 'true';
    form.querySelectorAll('[data-bdh-predictive-panel], .bdh__predictive-panel').forEach((panel) => panel.remove());
    form.classList.remove('bdh__search--enhanced', 'bdh__search--predictive-open');
    normalizeForm(form, input);
    ensureResetButton(form);
    input.addEventListener('input', () => syncInputs(input.value, input));
    form.addEventListener('reset', () => window.setTimeout(() => syncInputs('', input), 0));
    if (!window.routes?.predictive_search_url) return null;
    ensurePredictiveMarkup(form, input, index);
    let wrapper = form.closest('predictive-search');
    if (!wrapper) {
      wrapper = document.createElement('predictive-search');
      wrapper.className = 'bdh__predictive-search';
      wrapper.setAttribute('data-loading-text', LOADING_TEXT);
      form.parentNode.insertBefore(wrapper, form);
      wrapper.appendChild(form);
    } else {
      wrapper.classList.add('bdh__predictive-search');
      wrapper.setAttribute('data-loading-text', wrapper.getAttribute('data-loading-text') || LOADING_TEXT);
    }
    return wrapper;
  }

  function init() {
    installStyles();
    const wrappers = [];
    document.querySelectorAll(FORM_SELECTOR).forEach((form, index) => {
      const wrapper = enhanceForm(form, index);
      if (wrapper) wrappers.push(wrapper);
    });
    if (wrappers.length) {
      if (customElements.get('predictive-search')) wrappers.forEach(manuallyBootPredictive);
      else if (window.customElements?.whenDefined) customElements.whenDefined('predictive-search').then(() => wrappers.forEach(manuallyBootPredictive)).catch(() => {});
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();

(function initBDProductVariantMediaSync() {
  if (window.BDProductVariantMediaSyncReady) return;
  window.BDProductVariantMediaSyncReady = true;

  const ROOT_SELECTOR = '[id^="BeautyDentProduct-"][data-bdp]';
  const productCache = new Map();
  const syncedRoots = new WeakSet();

  function getProductHandle() {
    const canonicalHref = document.querySelector('link[rel="canonical"]')?.href;
    const sourceUrl = canonicalHref || window.location.href;
    try {
      const pathname = new URL(sourceUrl, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    }
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    return String(url).split('?')[0].replace(/^https?:/, '').replace(/_(60x60|80x80|100x100|120x120|160x160|180x180|300x300|400x400|600x600|800x800|1000x1000|1400x1400)(\.[a-zA-Z0-9]+)$/,'$2');
  }

  async function fetchProductData(handle) {
    if (!handle) return null;
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  }

  function getVariantImageIndex(productData, variantId, root) {
    if (!productData || !variantId) return null;
    const variant = (productData.variants || []).find((item) => String(item.id) === String(variantId));
    const featuredImage = variant?.featured_image;
    if (!featuredImage) return null;

    if (Number.isFinite(Number(featuredImage.position)) && Number(featuredImage.position) > 0) {
      return Number(featuredImage.position) - 1;
    }

    const targetUrls = [featuredImage.src, featuredImage.url, featuredImage.full].map(normalizeImageUrl).filter(Boolean);
    if (!targetUrls.length) return null;

    const domImages = Array.from(root.querySelectorAll('[data-bdp-image] img'));
    const domIndex = domImages.findIndex((img) => {
      const currentUrls = [img.currentSrc, img.src].map(normalizeImageUrl).filter(Boolean);
      return currentUrls.some((current) => targetUrls.some((target) => current === target || current.includes(target) || target.includes(current)));
    });

    return domIndex >= 0 ? domIndex : null;
  }

  function setActiveGalleryImage(root, index) {
    if (!Number.isFinite(Number(index)) || Number(index) < 0) return false;
    const targetIndex = String(Number(index));
    let changed = false;

    root.querySelectorAll('[data-bdp-gallery]').forEach((gallery) => {
      const single = gallery.dataset.single !== 'false';
      const images = gallery.querySelectorAll('[data-bdp-image]');
      const thumbs = gallery.querySelectorAll('[data-bdp-thumb]');

      if (!images.length || !images[Number(index)]) return;

      images.forEach((image) => {
        const active = image.dataset.bdpImage === targetIndex;
        image.classList.toggle('is-active', active);
        if (single) image.hidden = !active;
        else image.toggleAttribute('data-bdp-selected', active);
      });

      thumbs.forEach((thumb) => {
        const active = thumb.dataset.bdpThumb === targetIndex;
        thumb.classList.toggle('is-active', active);
        if (active && typeof thumb.scrollIntoView === 'function') {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      });

      changed = true;
    });

    return changed;
  }

  async function syncRootToSelectedVariant(root) {
    const variantSelect = root.querySelector('select[data-bdp-variant][name="id"], select[name="id"][data-bdp-variant]');
    if (!variantSelect?.value) return;

    const handle = getProductHandle();
    const productData = await fetchProductData(handle);
    const index = getVariantImageIndex(productData, variantSelect.value, root);
    setActiveGalleryImage(root, index);
  }

  function setupRoot(root) {
    if (!root || syncedRoots.has(root)) return;
    const variantSelect = root.querySelector('select[data-bdp-variant][name="id"], select[name="id"][data-bdp-variant]');
    if (!variantSelect) return;

    syncedRoots.add(root);
    variantSelect.addEventListener('change', () => syncRootToSelectedVariant(root));
    syncRootToSelectedVariant(root);
  }

  function init(context = document) {
    context.querySelectorAll?.(ROOT_SELECTOR).forEach(setupRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
